import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { eqlAggregate, eqlQuery } from "../../../../apis/apiEQL";
import { getGeneralDetails } from "../../../../apis/apiClientConfig";
import { BANDAS, calcular } from "../ethicalScore";
import { interpretar } from "../scoreMessages";

// Plantilla declarada en Configuración > General. Se pide una sola vez por
// sesión de la pantalla: no cambia al mover el selector de año.
let employeesPromise = null;
const fetchEmployees = () => {
  if (!employeesPromise) {
    employeesPromise = getGeneralDetails().then(
      (resp) => Number(resp?.general?.employees?.number_of_employees) || 0
    );
  }
  return employeesPromise;
};

/**
 * Meses desde el primer reporte del tenant, como proxy de la antigüedad del
 * canal. Sirve para no leer como desconfianza el volumen bajo de un programa
 * recién lanzado (observación `adopcion_temprana`). Se pide una sola vez.
 */
let launchPromise = null;
const fetchMonthsSinceLaunch = () => {
  if (!launchPromise) {
    launchPromise = eqlQuery({
      fields: ["created_at"],
      limit: 1,
      orderBy: "created_at",
    })
      .then((resp) => {
        const first = resp?.data?.[0]?.created_at;
        if (!first) return null;
        const d = new Date(String(first).replace(" ", "T"));
        if (Number.isNaN(d.getTime())) return null;
        const now = new Date();
        return (
          (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth())
        );
      })
      .catch(() => null);
  }
  return launchPromise;
};

const yearFilter = (year) => ({
  AND: [
    { field: "created_at", operator: "gte", value: `${year}-01-01` },
    { field: "created_at", operator: "lte", value: `${year}-12-31` },
  ],
});

// Denuncias del periodo, sin borradores (mismo criterio que los indicadores).
const countReports = async (year) => {
  const resp = await eqlAggregate({
    groupBy: "status.current",
    exclude: ["draft"],
    filters: yearFilter(year),
  });
  return resp?.metadata?.total_shown ?? 0;
};

/**
 * Meses cubiertos por el periodo. El año en curso sólo lleva los meses
 * transcurridos; anualizar sobre 12 lo haría ver artificialmente silencioso.
 */
const monthsElapsed = (year) => {
  const now = new Date();
  return year === now.getFullYear() ? now.getMonth() + 1 : 12;
};

/**
 * useEthicalScore — Score Ético del periodo, listo para el gauge.
 *
 * Entradas: plantilla declarada (Configuración > General) y total de denuncias
 * del año seleccionado. El cálculo vive en `../ethicalScore`; aquí sólo se
 * arma el payload del chart.
 *
 * Como sólo se alimentan volumen y represalias (esta última sin datos, ver
 * nota abajo), el score de hoy es en la práctica el subscore de volumen: qué
 * tan cerca está la tasa por 100 empleados del benchmark NAVEX.
 *
 * OJO: `calcular` asume `casosRepresalias: 0` cuando no se le pasa el dato, y
 * eso puntúa represalias como perfecto (5/5) con peso 0.25. Mientras no haya
 * un campo de represalias que alimentar, el score sale optimista por diseño
 * del módulo.
 *
 * @param {number} year
 * @returns {{ scoreChart: Object|null, loading: boolean, reason: string|null }}
 */
export const useEthicalScore = (year) => {
  const [t] = useTranslation("dashboard");
  const tRef = useRef(t);
  tRef.current = t;

  const cacheRef = useRef(new Map()); // year -> { scoreChart, reason }
  const [state, setState] = useState({ scoreChart: null, reason: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    if (cacheRef.current.has(year)) {
      setState(cacheRef.current.get(year));
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const [empleados, casosUnicos, mesesDesdeLanzamiento] = await Promise.all([
          fetchEmployees(),
          countReports(year),
          fetchMonthsSinceLaunch(),
        ]);
        if (!active) return;

        // Sin plantilla no hay denominador: se avisa en vez de inventar un score.
        if (!(empleados > 0)) {
          const built = { scoreChart: null, reason: "no_employees" };
          cacheRef.current.set(year, built);
          setState(built);
          return;
        }

        const meses = monthsElapsed(year);
        const periodo = { empleados, casosUnicos, meses };
        const r = calcular(periodo);
        const insights = interpretar(r, periodo, { mesesDesdeLanzamiento });
        const tr = tRef.current;

        const banda = tr(`static_dashboard.ethical_grade_${r.grade}`);
        const msg = (k, o) => tr(`static_dashboard.ethical_messages.${k}`, o);
        // El pie dice el diagnóstico, no sólo los insumos: cuando falta volumen
        // se enuncia la brecha contra el referente.
        const subtitulo =
          insights.estado !== "saludable" && insights.contexto.brecha > 0
            ? msg("subtitle_gap", {
                banda,
                expected: insights.contexto.esperadas.toLocaleString(),
                received: insights.contexto.recibidas.toLocaleString(),
              })
            : msg("subtitle_state", {
                banda,
                state: msg(`estados.${insights.estado}.titulo`),
              });

        const built = {
          reason: null,
          scoreChart: {
            id: "ethical-score",
            title: tr("static_dashboard.ethical_score"),
            type: "gauge",
            chartData: {
              title: tr("static_dashboard.ethical_score"),
              gauge: {
                value: r.riesgo,
                max: 100,
                // El rótulo sobre el arco es la palabra de la banda, traducida.
                bands: BANDAS.map((b) => ({
                  ...b,
                  grade: tr(`static_dashboard.ethical_grade_${b.grade}`),
                })),
                label: banda,
              },
            },
            // Fuera del canvas del chart: ahí sí caben las líneas largas.
            footer: subtitulo,
            footerNote: tr("static_dashboard.ethical_score_basis", {
              cases: casosUnicos.toLocaleString(),
              employees: empleados.toLocaleString(),
            }),
            insights,
            // Se exponen para depurar / futuros detalles en la tarjeta.
            result: r,
            inputs: { empleados, casosUnicos, meses },
          },
        };
        cacheRef.current.set(year, built);
        setState(built);
      } catch (e) {
        console.error("useEthicalScore: failed", e);
        if (active) setState({ scoreChart: null, reason: "error" });
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [year]);

  return { ...state, loading };
};

export default useEthicalScore;
